import { useEffect, useState, type ReactElement } from 'react';
import { aehnlicheItems } from '@schnitt/shared';
import { api, type RoadmapItem } from '../api';
import { Sheet } from './Sheet';
import { bericht } from '../tonkette';
import { bildBericht, bildSatz, bildZeile, leinwandZeile } from '../bildbericht';

/**
 * Etwas melden -- von jedem Bildschirm aus.
 *
 * Der Bereich ist vorausgefüllt, weil der Knopf weiß, wo er steht: eine
 * Meldung aus dem Schnittplatz muss nicht erklären, dass sie aus dem
 * Schnittplatz kommt. Der Screenshot kommt per Einfügen herein, so wie Sascha
 * ihn ohnehin gerade im Bild hat.
 */
export function FeedbackSheet({
  onClose,
  onDone,
  bereich = '',
}: {
  onClose: () => void;
  /** Wird nach dem Abschicken gerufen; der Fahrplan lädt sich damit neu. */
  onDone?: () => Promise<void> | void;
  /** Wo der Knopf gedrückt wurde -- vorausgefüllt, damit niemand tippen muss. */
  bereich?: string;
}): ReactElement {
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [area, setArea] = useState(bereich);
  const [bild, setBild] = useState<{ blob: Blob; url: string } | null>(null);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Was schon in der Roadmap steht -- einmal geholt, danach beim Tippen nur
  // noch durchsucht. Ein Server-Weg je Tastendruck wäre Unfug.
  const [vorhanden, setVorhanden] = useState<RoadmapItem[]>([]);
  const [gestimmt, setGestimmt] = useState<Set<string>>(new Set());
  useEffect(() => {
    void api
      .roadmap()
      .then(({ items }) => setVorhanden(items.filter((i) => i.status !== 'abgelehnt' && i.status !== 'entfallen')))
      .catch(() => setVorhanden([]));
  }, []);
  const aehnlich = aehnlicheItems(`${title} ${detail}`.trim(), vorhanden).slice(0, 3);
  // Welche Version hier läuft -- wer etwas meldet, liest sie gleich mit
  // (Sascha, 28. Oktober: „und auch irgendwo eingeblendet“).
  const [version, setVersion] = useState<string | null>(null);
  useEffect(() => {
    void api
      .version()
      .then((v) => setVersion(v.release))
      .catch(() => setVersion(null));
  }, []);

  /**
   * Wie es dem Ton auf *diesem* Gerät geht.
   *
   * „Ich kann leider den Ton am Handy nicht hören. Aber am Desktop
   * funktioniert es wunderbar“ (Sascha, 7. September) -- und dann stand hier
   * das Raten. Drei ganz verschiedene Ursachen sehen von aussen gleich aus:
   * die Tonkette schläft, die Sitzung ist nicht offen (dann gilt auf dem
   * iPhone der kleine Schalter an der Seite), oder eine Datei liess sich nicht
   * dekodieren. Deshalb stehen sie hier, in Worten, und gehen mit der Meldung
   * mit.
   */
  const ton = bereich === 'Schnittplatz' ? bericht() : null;
  const tonText = ton
    ? [
        `Tonkette: ${{ wach: 'wach', 'schläft': 'schläft', aus: 'nicht gebaut', zu: 'geschlossen' }[ton.kette]}`,
        `Wiedergabe-Sitzung: ${ton.sitzung ? 'offen' : 'zu'}`,
        `Aufnahmen: ${ton.bereit} bereit, ${ton.laedt} laden, ${ton.gehtNicht} gehen nicht`,
        `Klingt gerade: ${ton.klingt}`,
      ].join(' · ')
    : '';
  /** Und dasselbe fürs Bild -- aus demselben Grund (7. September, abends). */
  const flaechen = bereich === 'Schnittplatz' ? bildBericht() : [];
  const leinwand = bereich === 'Schnittplatz' ? leinwandZeile() : '';
  const bildText = [...flaechen.map(bildZeile), ...(leinwand ? [leinwand] : [])].join(' | ');
  const bildSatzText = flaechen.length ? bildSatz(flaechen) : '';
  const tonSatz = !ton
    ? ''
    : ton.kette !== 'wach'
      ? 'Die Tonkette schläft noch — sie wacht beim ersten Tippen auf Abspielen auf.'
      : !ton.sitzung
        ? 'Die Wiedergabe-Sitzung ist zu. Auf dem iPhone entscheidet dann der kleine Schalter an der Seite, ob überhaupt etwas zu hören ist.'
        : ton.gehtNicht > 0
          ? `${ton.gehtNicht} Aufnahme(n) liessen sich nicht aufbereiten — die laufen über den alten Weg und können auf dem iPhone stumm bleiben.`
          : 'Der Ton sollte gehen. Hörst du trotzdem nichts, sind es die Lautstärketasten oder der Schalter an der Seite.';

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const datei = [...(event.clipboardData?.items ?? [])].find((i) => i.type.startsWith('image/'))?.getAsFile();
      if (!datei) return;
      event.preventDefault();
      setBild({ blob: datei, url: URL.createObjectURL(datei) });
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  const senden = async () => {
    setSendet(true);
    setFehler(null);
    try {
      const { item } = await api.roadmapFeedback({
        title: title.trim(),
        // Die Angaben zum Ton gehen mit -- ohne sie ist „kein Ton“ eine
        // Meldung, mit der niemand etwas anfangen kann.
        detail: [
          detail.trim(),
          tonText ? `\n\n— Ton: ${tonText}` : '',
          bildText ? `\n— Bild: ${bildText}` : '',
        ]
          .join('')
          .trim(),
        area: area.trim(),
      });
      if (bild) await api.roadmapImage(item.id, bild.blob, 'screenshot.png');
      await onDone?.();
      onClose();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Das ließ sich nicht abschicken.');
      setSendet(false);
    }
  };

  return (
    <Sheet title="Etwas melden" onClose={onClose}>
      <label className="field">
        <span>Worum geht es?</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ton-Clips brauchen eigene Werkzeuge" />
      </label>
      <label className="field">
        <span>Und ausführlicher</span>
        <textarea rows={4} value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Was ist passiert, was hättest du erwartet?" />
      </label>
      <label className="field">
        <span>Wo war das?</span>
        <input value={area} onChange={(e) => setArea(e.target.value)} placeholder="Schnittplatz, Medien, Startseite …" />
      </label>
      {/* Bevor jemand etwas zum dritten Mal vorschlägt: zeigen, was schon
          dasteht. Aus einer Doppelung wird so eine Stimme mehr -- und das ist
          die ehrlichere Zahl. */}
      {aehnlich.length ? (
        <div className="card" style={{ marginBottom: 10, padding: '10px 12px' }} data-aehnlich>
          <div className="hint" style={{ marginTop: 0 }}>Steht vielleicht schon in der Roadmap:</div>
          {aehnlich.map(({ item }) => (
            <div key={item.id} className="spread" style={{ alignItems: 'center', gap: 8, marginTop: 8 }}>
              <span>
                {item.key ? <span className="plan-key">{item.key}</span> : null} {item.title}
              </span>
              <button
                className={`chip ${gestimmt.has(item.id) ? 'active' : ''}`}
                disabled={gestimmt.has(item.id)}
                onClick={async () => {
                  await api.roadmapVote(item.id, 1);
                  setGestimmt((alt) => new Set([...alt, item.id]));
                }}
              >
                {gestimmt.has(item.id) ? 'Stimme gezählt' : 'Dafür stimmen'}
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {ton ? (
        <div className="card" style={{ marginBottom: 10, padding: '10px 12px' }} data-ton>
          <div className="hint" style={{ marginTop: 0 }}>Ton auf diesem Gerät</div>
          <p style={{ margin: '6px 0 4px' }}>{tonSatz}</p>
          <div className="hint" style={{ marginTop: 0, fontVariantNumeric: 'tabular-nums' }}>{tonText}</div>
          {flaechen.length ? (
            <>
              <div className="hint" style={{ marginTop: 10 }}>Bild auf diesem Gerät</div>
              <p style={{ margin: '6px 0 4px' }}>{bildSatzText}</p>
              {flaechen.map((b) => (
                <div key={b.flaeche} className="hint" style={{ marginTop: 0, fontVariantNumeric: 'tabular-nums' }} data-bildzeile>
                  {bildZeile(b)}
                </div>
              ))}
              {leinwand ? (
                <div className="hint" style={{ marginTop: 0, fontVariantNumeric: 'tabular-nums' }} data-leinwandzeile>
                  {leinwand}
                </div>
              ) : null}
            </>
          ) : null}
          <div className="hint" style={{ marginTop: 6 }}>Diese Angaben gehen mit, wenn du abschickst.</div>
        </div>
      ) : null}
      <p className="hint">
        Screenshot hier <b>einfügen</b> (⌘V, oder lange tippen → Einsetzen).
      </p>
      {bild ? <img src={bild.url} alt="" style={{ borderRadius: 10, width: '100%', marginBottom: 8 }} /> : null}
      {fehler ? <p className="hint" style={{ color: 'var(--err-text)' }}>{fehler}</p> : null}
      <button className="primary block" disabled={title.trim().length < 3 || sendet} onClick={() => void senden()}>
        {sendet ? 'Wird geschickt…' : 'Abschicken'}
      </button>
      {version ? (
        <p className="hint" style={{ textAlign: 'center', marginTop: 8 }} data-feedback-version>
          Version {version}
        </p>
      ) : null}
    </Sheet>
  );
}
